/**
 * /policies/eos/notice-period-rules — Session 3c PR 27.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { NoticePeriodRuleItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function EosNoticePeriodPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-eos-notice-period", le],
    queryFn: () => session3cApi.listNoticePeriodRules(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<NoticePeriodRuleItem>[] = [
    {
      headerKey: "policies.eos_notice.col.grade",
      label: "Grade",
      render: (r) => <span className="font-medium">{r.grade_code}</span>,
    },
    {
      headerKey: "policies.eos_notice.col.days",
      label: "Notice days",
      render: (r) => <span className="font-mono">{r.notice_days.value}</span>,
      traceableValue: (r) => ({ label: t("policies.eos_notice.col.days", "Notice days"), stack: r.notice_days }),
    },
    {
      headerKey: "policies.eos_notice.col.pil",
      label: "Pay-in-lieu",
      render: (r) => <span className="text-xs">{r.pay_in_lieu_allowed ? "yes" : "no"}</span>,
    },
    {
      headerKey: "policies.eos_notice.col.waivable",
      label: "Waivable",
      render: (r) => <span className="text-xs text-text-secondary">{r.waivable_conditions.join(" · ") || "—"}</span>,
    },
  ];

  return (
    <PolicyListPage<NoticePeriodRuleItem>
      title={t("policies.eos_notice.title", "Notice Period Rules")}
      subtitle={t("policies.eos_notice.subtitle", "Per-grade notice days · pay-in-lieu option · waivable conditions. Mutaba'aa vetoes anything below 30-day Article 75 floor.")}
      agent="mutabaa"
      domain="eos_notice"
      newButtonLabel={t("policies.eos_notice.cta.new", "New Notice Rule")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.eos_notice.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.eos_notice.suggestion.clean_body", "All notice rules at or above 30-day Article 75 floor for indefinite contracts. Pay-in-lieu allowed per rule."),
          primaryActionLabel: t("policies.eos_notice.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "eos-notice-period-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.eos_notice.title", "Notice Period Rules"),
        window_label: t("policies.eos_notice.window", "across grades"),
      }}
    />
  );
}
